import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { EditorJsField } from '@/components/editor/EditorJsField'
import { exercisesApi } from '@/api/exercises'
import { wordsApi } from '@/api/words'
import { getErrorMessage } from '@/utils/error'
import type { EditorJsData } from '@/types'

export type ElementKind = 'theory' | 'vocabulary'

export interface WordOption {
  id: number
  label: string
}

/** Что редактируем: блок шаблона или блок копии назначения — форма одна */
export interface ElementDraft {
  id: number
  type: string
  title?: string | null
  body_json?: EditorJsData | Record<string, unknown> | null
  words?: Array<{ id: number; text?: string; word?: string; translation?: string | null }>
}

export interface ElementPayload {
  type: ElementKind
  title: string | null
  body_json?: Record<string, unknown>
  word_ids?: number[]
}

/**
 * Блок темы: теория или словарь.
 *
 * Форма общая для шаблона программы и для копии, назначенной ученику — разница
 * только в том, куда уходит сохранение. Держать её в двух местах значило бы
 * чинить правку теории дважды.
 *
 * Тип «Задание» открывает свой конструктор: он приходит сюда через
 * `onSwitchToExercise` — вход в блок один на все типы.
 */
export function ThemeElementDialog({
  open,
  themeTitle,
  element,
  readOnly = false,
  onSave,
  onClose,
  onSwitchToExercise,
}: {
  open: boolean
  themeTitle: string
  /** null — создание нового блока */
  element: ElementDraft | null
  readOnly?: boolean
  onSave: (payload: ElementPayload) => Promise<void>
  onClose: () => void
  onSwitchToExercise?: () => void
}) {
  const { t } = useTranslation()

  const [type, setType] = useState<ElementKind>('theory')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState<EditorJsData>({ blocks: [] })
  const [wordOptions, setWordOptions] = useState<WordOption[]>([])
  const [words, setWords] = useState<WordOption[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    setError(null)
    setType((element?.type as ElementKind) ?? 'theory')
    setTitle(element?.title ?? '')
    setBody((element?.body_json as EditorJsData | undefined) ?? { blocks: [] })
    setWords(
      (element?.words ?? []).map((word) => ({
        id: word.id,
        label: word.translation
          ? `${word.word ?? word.text ?? ''} — ${word.translation}`
          : (word.word ?? word.text ?? ''),
      })),
    )
  }, [open, element])

  const searchWords = async (query: string) => {
    try {
      const result = await wordsApi.getWords({ search: query, per_page: 20 })
      setWordOptions(
        result.data.map((word) => ({
          id: word.id,
          label: word.translation ? `${word.word} — ${word.translation}` : word.word,
        })),
      )
    } catch {
      setWordOptions([])
    }
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      // Матрица типов на сервере: теория требует тело и запрещает слова,
      // словарь — наоборот. Отправляем ровно то, что положено типу.
      await onSave({
        type,
        title: title || null,
        body_json: type === 'theory' ? (body as unknown as Record<string, unknown>) : undefined,
        word_ids: type === 'vocabulary' ? words.map((word) => word.id) : undefined,
      })
      onClose()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        {/* Тип блока в шапке: у существующего он не меняется, и отдельное поле
            было бы мёртвым контролом */}
        {element ? t(`programs.elementTypes.${type}`) : t('programs.addElementTo', { theme: themeTitle })}
        {element && (
          <Typography variant="body2" color="text.secondary">
            {themeTitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {/* Тип задаётся только при создании: у существующего блока он не
            меняется — копии в назначениях перевернулись бы вместе с ним */}
        {!element && !readOnly && (
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel>{t('programs.fields.elementType')}</InputLabel>
            <Select
              label={t('programs.fields.elementType')}
              value={type}
              onChange={(event) => {
                const next = event.target.value as ElementKind | 'exercise'

                if (next === 'exercise') {
                  onSwitchToExercise?.()

                  return
                }

                setType(next)
              }}
            >
              <MenuItem value="theory">{t('programs.elementTypes.theory')}</MenuItem>
              <MenuItem value="vocabulary">{t('programs.elementTypes.vocabulary')}</MenuItem>
              {onSwitchToExercise && (
                <MenuItem value="exercise">{t('programs.elementTypes.exercise')}</MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {/* В просмотре — текст, а не поле ввода: поле ловит фокус на клик и
            выглядит как приглашение править то, что править нельзя */}
        {readOnly ? (
          title.trim() !== '' && (
            <Typography variant="h6" sx={{ mt: 1 }}>
              {title}
            </Typography>
          )
        ) : (
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label={t('programs.fields.elementTitle')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        )}

        {type === 'theory' ? (
          <Box sx={{ mt: 2 }}>
            <EditorJsField
              value={body}
              onChange={setBody}
              label={t('programs.fields.theory')}
              readOnly={readOnly}
              readOnlySurface="paper"
              extendedBlocks
              hideLabel={readOnly}
              // Картинка в теории идёт в квоту учителя тем же путём, что и
              // картинки заданий: эндпоинт проверяет место ДО записи файла
              imageUploader={readOnly ? undefined : (file) => exercisesApi.uploadImage(file)}
              initDelayMs={100}
            />
          </Box>
        ) : readOnly ? (
          <Box sx={{ mt: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {words.length === 0 ? (
              <Typography color="text.secondary">{t('programs.themeEmpty')}</Typography>
            ) : (
              words.map((word) => <Chip key={word.id} size="small" variant="outlined" label={word.label} />)
            )}
          </Box>
        ) : (
          <Autocomplete
            multiple
            options={wordOptions}
            value={words}
            sx={{ mt: 2 }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(_, value) => value.length >= 2 && void searchWords(value)}
            onChange={(_, value) => setWords(value)}
            renderInput={(params) => (
              <TextField {...params} label={t('programs.fields.words')} size="small" />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? t('common.close') : t('common.cancel')}</Button>
        {!readOnly && (
          <Button variant="contained" disabled={saving} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
